// 2) Third-party library imports
import { fetchAuthSession } from 'aws-amplify/auth';
import {
    cloneDeep,
    concat,
    get,
    isEmpty,
    isUndefined,
    map
} from 'lodash-es';
import {
    all,
    call,
    fork,
    put,
    takeLatest,
    delay,
    select
} from 'redux-saga/effects';

// 3a) Constants
import {
    API_NAME,
    maxAPIRefetchCount,
    refetchAPIDelay
} from '../../config/config';
import {
    CUSTOMERS_PAGE,
    DETAILS_TAB,
    INVOICES_PAGE
} from '../../config/tableAndPageConstants';
import {
    FamilyNameAttribute,
    GivenNameAttribute
} from '../../constants/authUserAttributes';
import { AtbViewType } from '../../constants/settings';

// 3c) Project-specific modules - components / types
import dayjs from 'src/config/dayjs';
import { _queries as queries } from '../../graphql/queries.graphql';
import { Conversation, PageData } from '../common/types';
import {
    Invoice,
    InvoicesFetchedData
} from './types';
import { Customer } from '../customers/types';
import { ChangeLinesFetchedData } from '../changes/types';
import { ConversationLinesFetchedData } from '../conversationLine/types';
import type { RootState } from '../storeTypes';
import { dispatchAction } from '../storeInstance';

// 3d) Utility functions
import {
    removeAppliedFiltersForApiRequest,
    getSortFieldsWithCustomFields,
    checkShouldRequestRefetch,
    formatDateToDateObjectUTC,
    getRegionConfigFromList,
    getCompanyFlagValue,
    getRegionallyGraphqlResponse,
    getGraphqlQueryString} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

// 3e) Redux actions
import { getCurrentUser, getRegionKeyConfig, getRegionSettingsConfig } from '../auth/authSlice';
import { getCloudImportType } from '../companies/companySlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import client, { post } from 'src/api';
import {
    getInvoiceConversationErrorAction,
    getInvoiceConversationSuccessAction,
    getInvoiceDataErrorAction,
    getInvoiceDataSuccessAction,
    getInvoicesErrorAction,
    getInvoicesSuccessAction,
    setSelectedInvoiceIdSuccessAction,
    getInvoicesRequestAction,
    getInvoiceChangesSuccessAction,
    getInvoiceChangesErrorAction,
    getInvoicesForOrganisationRequestAction,
    invoicesFetchedForOrganisationAction,
    invoiceConversationLinesFetchedForOrganisationAction,
    getInvoiceChangeLinesSuccessAction,
    invoiceChangeLinesFetchedForOrganisationAction,
    getOrganisationInvoiceChangesRequestAction,
    getInvoicesForPaymentPlanRequestAction,
    getInvoiceCustomFieldsForCompanyRequestAction,
    getInvoiceCustomFieldsForOrganisationRequestAction,
    getInvoiceChangesRequestAction,
    invoicePauseResumeRequestAction,
    checkInvoicesPauseResumeStateAction,
    organisationInvoiceAddCommentRequestAction,
    invoiceAddCommentRequestAction,
    getOrganisationInvoiceConversationRequestAction,
    getInvoiceConversationRequestAction,
    raiseQueryInvoiceBulkRequestAction,
    checkInvoicesIfSingleCustomerAction,
    setSelectedInvoiceIdRequestAction,
    getOrganisationInvoiceDataRequestAction,
    getInvoiceDataRequestAction,
    getInvoicesPageData,
    getPaymentPlanConfigTemplateFailedAction,
    getPaymentPlanConfigTemplateSuccessfullyAction,
    getInvoicesHaveNoPaymentPlanFailedAction,
    getInvoicesHaveNoPaymentPlanSuccessfullyAction,
    getPaymentPlanConfigTemplateRequestAction,
    getInvoicesHaveNoPaymentPlanRequestAction,
    updateBatchPaymentPlanInvoiceFilters,
    updateBatchPaymentPlanCustomerFilters,
    getCustomersHaveNoPaymentPlanSuccessfullyAction,
    getCustomersHaveNoPaymentPlanRequestAction,
    getCustomersHaveNoPaymentPlanFailedAction,
    updateInvoicesHaveNoPaymentPlanDataAction,
    updateBatchPaymentPlanFormDataAction,
    getCustomerInvoicesForCompanyRequestAction,
    getCustomerInvoicesForCompanyFailedAction,
    updateBatchPaymentPlanFormDataRequestAction,
    submitBatchPaymentPlanAction,
    getInvoiceCustomers
} from './invoiceSlice';

const cleanCustomFieldsFilter = (filters: any): any => {
    if (!filters) return null;

    const cleanedFilters = cloneDeep(filters);
    const customFieldMultipleValues = get(cleanedFilters, 'CustomFieldMultipleValuesFilters', null);

    if (customFieldMultipleValues) {
        cleanedFilters.CustomFieldMultipleValuesFilters = JSON.parse(customFieldMultipleValues);
    }

    const customField = get(cleanedFilters, 'CustomFieldFilters', null);

    if (customField) {
        cleanedFilters.CustomFieldFilters = JSON.parse(customField);
    }

    return cleanedFilters;
};

let refetchCount = 0;

function * handleSubmitBatchPaymentPlanRequest({ payload }: any) {
    const { 
        formData, 
        customerManagementFilter,
        invoiceManagementFilter,
        callback 
    } = payload;
    const response: { IsSuccess: boolean, Messages: string[] } = {
        IsSuccess: true,
        Messages: [],
    };

    try {
        const excludedItems = get(formData, 'excludedItems', []);

        let resolvedInvoiceFilter = invoiceManagementFilter;
        let resolvedCustomerFilter = customerManagementFilter;

        if (excludedItems.length > 0 && invoiceManagementFilter) {
            const updatedInvoiceIds = invoiceManagementFilter.ExcludeInvoices === true
                ? concat(invoiceManagementFilter.InvoiceIds, excludedItems.map((item: any) => item.InvoiceId))
                : (invoiceManagementFilter.InvoiceIds || []).filter((id: string) => !excludedItems.some((item: any) => item.InvoiceId === id));

            resolvedInvoiceFilter = { ...invoiceManagementFilter, InvoiceIds: updatedInvoiceIds };
        } else if (excludedItems.length > 0 && customerManagementFilter) {
            const updatedCustomerIds = customerManagementFilter.ExcludeCustomers === true
                ? concat(customerManagementFilter.CustomerIds, excludedItems.map((item: any) => item.CustomerId))
                : (customerManagementFilter.CustomerIds || []).filter((id: string) => !excludedItems.some((item: any) => item.CustomerId === id));

            resolvedCustomerFilter = { ...customerManagementFilter, CustomerIds: updatedCustomerIds };
        }

        const requestBody = {
            PaymentPlanConfig: get(formData, 'paymentPlanConfig'),
            RequestFromPage: get(formData, 'requestFromPage'),
            IsSkipNotifyCustomer: get(formData, 'skipCustomerNotification'),
            // IsIndividualInvoice: get(formData, 'isIndividualInvoice'),
            CustomerManagementFilter: cleanCustomFieldsFilter(resolvedCustomerFilter),
            InvoiceManagementFilter: cleanCustomFieldsFilter(resolvedInvoiceFilter)
        };

        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/paymentplan/bulk/validate',
            options: {
                body: requestBody
            }
        });
        const res: DynamicObject = yield body.json();

        if (isEmpty(get(res, 'AcceptRequest', {}))) {
            throw new Error('Failed to validate the batch payment plan.');
        }

        response.Messages.push(`Your Batch Payment Plan request has been started. You will receive a notification once the process is complete.`);

        if (callback) {
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error 
                && !isUndefined(get(err, 'response'))  
                && !isUndefined((err as any).response.data)) {
                returnData = (err as any).response.data;
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

function* handleUpdateBatchPaymentPlanFormData({ payload }: any) {
    const { formData, refetchInvoicesPayload } = payload;

    try {
        yield put(updateBatchPaymentPlanFormDataAction(formData));

        if (refetchInvoicesPayload) {
            yield put(getCustomerInvoicesForCompanyRequestAction(refetchInvoicesPayload));
        }
    } catch(err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }
    }
};

function* handleGetInvoicesHaveNoPaymentPlanRequest({ payload }: any) {
    try {
        const {
            invoiceManagementFilter,
            pageSize,
            currentPage
        } = payload;

        const commonErrorMessage = 'Failed to fetch invoices no payment plan for company.';
        let res: DynamicObject | undefined = undefined;
        let invoices: Invoice[] = [];

        if (invoiceManagementFilter) {
            const {
                filters,
                invoiceState,
                sortBy,
                sortAscending,
                excludeInvoices,
                customerIds,
                invoiceIds,
                invoiceCode,
                customerCode,
                customer,
                hasPaymentPlan
            } = invoiceManagementFilter;

            let cleanFilters = removeAppliedFiltersForApiRequest(
                filters,
                true,
                'invoice',
                true
            );

            if (!cleanFilters.AmountValue) {
                cleanFilters = {
                    ...cleanFilters,
                    AmountOperator: '>',
                    AmountType: 'Owing',
                    AmountValue: 0
                }
            }

            let filteredCustomerIds = customerIds;

            if (cleanFilters.Customer) {
                const invoiceCustomers: Customer[] = yield select(getInvoiceCustomers);
                const customerFilterValue = cleanFilters.Customer.toLowerCase();
                          
                const matchedCustomer = invoiceCustomers.find(c =>
                    c.DisplayName && c.DisplayName.toLowerCase() === customerFilterValue ||
                    c.CustomerCode && c.CustomerCode.toLowerCase() === customerFilterValue
                );
              
                if (matchedCustomer) {
                  filteredCustomerIds = [matchedCustomer.Id];
                }
            }

            const sortObject = getSortFieldsWithCustomFields(invoiceManagementFilter.sortBy);
            const requestFilter = {
                InvoiceState: invoiceState,
                SortField: sortBy,
                ...sortObject,
                Customer: customer,
                CustomerCode: customerCode,
                InvoiceIds: invoiceIds,
                CustomerIds: filteredCustomerIds,
                InvoiceCode: invoiceCode,
                ...cleanFilters,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * INVOICES_PAGE.pageSize,
                ExcludeInvoices: excludeInvoices,
                HasPaymentPlan: hasPaymentPlan
            };

            res = yield call(
                [client, 'graphql'],
                {
                    query: queries.GET_INVOICES_FOR_COMPANY,
                    variables: {
                        ... requestFilter
                    }
                }
            );

            if (!res) {
                throw new Error(commonErrorMessage);
            }

            invoices = get(res.data, 'GetInvoicesForCompany.Invoices');

            if (invoices.length > 0) {
                if (customer) {
                    invoices = invoices.filter((invoice: Invoice) => {
                        return invoice.Customer && (
                            invoice.Customer.CustomerCode.toLocaleLowerCase() === customer.toLocaleLowerCase() || 
                            invoice.Customer.DisplayName.toLocaleLowerCase() === customer.toLocaleLowerCase()
                        );
                    }); 
                }

                if (customerCode) {
                    invoices = invoices.filter((invoice: Invoice) => {
                        return invoice.Customer && (invoice.Customer.CustomerCode.toLocaleLowerCase() === customerCode.toLocaleLowerCase());
                    }); 
                }

                yield put(updateBatchPaymentPlanInvoiceFilters(requestFilter));
            }
        }

        const responsePayload = {
            data: invoices,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(invoices.length < pageSize) && !(pageSize < INVOICES_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(getInvoicesHaveNoPaymentPlanSuccessfullyAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getInvoicesHaveNoPaymentPlanRequestAction(payload));
        } else {
            yield put(getInvoicesHaveNoPaymentPlanFailedAction());
        }
    }
}

function* handleGetCustomersHaveNoPaymentPlanRequest({ payload }: any) {
    try {
        const { 
            customerManagementFilter,
            pageSize,
            currentPage
        } = payload;

        const commonErrorMessage = 'Failed to fetch customers no payment plan for company.';
        let customers: Customer[] = [];

        if (customerManagementFilter) {
            const {
                filters,
                atbType,
                sortBy,
                sortAscending,
                isUsingCloudImportType,
                usingCustomerWorkflow,
                usingMultipleWorkflow,
                customerIds,
                invoiceIds,
                invoiceCode,
                customerCode,
                customer,
                hasPaymentPlan, 
                excludeCustomers,
            } = customerManagementFilter;
            let cleanFilters = removeAppliedFiltersForApiRequest(
                filters,
                true,
                'customer',
                true
            );

            if (!cleanFilters.AmountValue) {
                cleanFilters = {
                    ...cleanFilters,
                    AmountOperator: '>',
                    AmountType: 'Total owing',
                    AmountValue: 0
                }
            }
            const cloudImportType: string = yield select(getCloudImportType);
            const sortObject = getSortFieldsWithCustomFields(sortBy);
            const requestFilter = {
                ...cleanFilters,
                ATBType: atbType,
                ...sortObject,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * CUSTOMERS_PAGE.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                UsingCustomerWorkflow: usingCustomerWorkflow,
                IsMultipleWorkflow: usingMultipleWorkflow,
                Customer: customer ? customer : customerCode,
                // CustomerCode: customerCode,
                InvoiceIds: invoiceIds,
                CustomerIds: customerIds,
                InvoiceCode: invoiceCode,
                HasPaymentPlan: hasPaymentPlan,
                ExcludeCustomers: excludeCustomers
            };

            const res: DynamicObject = yield call(
                [client, 'graphql'],
                {
                    query: queries.GET_CUSTOMERS_FOR_COMPANY,
                    variables: {
                        ... requestFilter
                    }
                }
            );
    
            customers = get(res.data, 'GetCustomersForCompany.Customers');

            if (!res) {
                throw new Error(commonErrorMessage);
            }

            if (customers.length > 0) {
                yield put(updateBatchPaymentPlanCustomerFilters(requestFilter));
            }
        }

        const responsePayload = {
            data: customers,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(customers.length < pageSize) && !(pageSize < CUSTOMERS_PAGE.pageSize)
            }
        };

        refetchCount = 0;
        yield put(getCustomersHaveNoPaymentPlanSuccessfullyAction(responsePayload));
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getCustomersHaveNoPaymentPlanRequestAction(payload));
        } else {
            yield put(getCustomersHaveNoPaymentPlanFailedAction());
        }
    }
}

function* handleGetPaymentPlanConfigTemplatesRequest() {
    try {
        const response: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_PAYMENT_PLAN_CONFIG_TEMPLATE,
                variables: {
                    IsTemplate: true
                }
            }
        );
        
        const { Configurations } = get(response.data, 'GetPaymentPlanConfig');

        if (Configurations) {
            const responsePayload = {
                data: Configurations
            }

            yield put(getPaymentPlanConfigTemplateSuccessfullyAction(responsePayload));
            refetchCount = 0;
        }
        
    } catch (error) {
        if (error instanceof Error) {
            console.log('Error', error);
        } else {
            console.error('An unknown error occured.', error);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(error)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getPaymentPlanConfigTemplateRequestAction());
        } else {
            yield put(getPaymentPlanConfigTemplateFailedAction());
        }
    }
}

/**
 * Function that fetches the invoice list - api connection.
 * @param param0
 */
function* handleGetInvoicesRequest({ payload }: any) {
    const errorMessage = 'Error fetching invoice list. Please try again later.';

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
            isPaymentPlanEnabled,
            excludeInvoices
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice',
            true
        );
        const cloudImportType: string = yield select(getCloudImportType);
        const sortObject = getSortFieldsWithCustomFields(sortBy);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    ...sortObject,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * INVOICES_PAGE.pageSize,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    IsMultipleWorkflow: usingMultipleWorkflow,
                    UsingCustomerWorkflow: usingCustomerWorkflow,
                    PaymentPlanEnabled: isPaymentPlanEnabled,
                    ExcludeInvoices: excludeInvoices
                }
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForCompany');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < INVOICES_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getInvoicesRequestAction(payload));
        } else {
            yield put(getInvoicesErrorAction([errorMessage]));
        }
    }
}

function* handleGetInvoicesForOrganisationRequest({ payload: sagaPayload }: any) {
    let { refetchCount, ...payload } = sagaPayload;
    refetchCount = refetchCount || 0;
    const errorMessage = 'Error fetching invoice list. Please try again later.';

    try {
        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
            region,
            conversionCurrency,
            organisationCurrencies,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice'
        );

        const sortObject = getSortFieldsWithCustomFields(sortBy);

        // Use region from payload to get correct API URL for the specific tab
        let apiUrl: string | undefined;
        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(getRegionKeyConfig);
            const regionSettingsConfig: DynamicObject[] = yield select(getRegionSettingsConfig);
            const configUsed = getRegionConfigFromList(region, regionKeyConfig, regionSettingsConfig);
            apiUrl = get(configUsed, 'Url');
        } else {
            apiUrl = yield select(getOrganisationActionUrl);
        }
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_INVOICES_FOR_ORGANISATION,
            {
                InvoiceState: invoiceState,
                ...cleanFilters,
                // SortField: sortBy,
                ...sortObject,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * INVOICES_PAGE.pageSize,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                UsingCustomerWorkflow: usingCustomerWorkflow,
                IsMultipleWorkflow: usingMultipleWorkflow,
                ConversionCurrency: conversionCurrency,
                OrganisationCurrencies: organisationCurrencies
            }
        );

        const { Invoices } = get(res.data, 'GetInvoicesForOrganisation');

        if (Invoices) {
            const responsePayload = {
                data: Invoices,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Invoices.length < pageSize) &&
                        !(pageSize < INVOICES_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getInvoicesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(getInvoicesForOrganisationRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getInvoicesErrorAction([errorMessage]));
        }
    }
}

/**
 * Function that calls the API for getting the invoice details based on the given Id.
 * @param param0
 */
function* handleGetInvoiceDataRequest({ payload: { invoiceId, isUsingCloudImportType, isInvoicePanelView }}: any) {
    const errorMessage =
        'Error fetching invoice details. Please try again later.';

    try {
        const isCalendarView: boolean = yield select(
            (state: RootState) => getCompanyFlagValue(state, AtbViewType.CalendarView)
        );
        const cloudImportType: string = yield select(getCloudImportType);
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICE_DETAILS_FOR_COMPANY,
                variables: {
                    InvoiceId: invoiceId,
                    IsCalendarView: isCalendarView,
                    IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                    IsInvoicePanelView: isInvoicePanelView
                }
            }
        );

        const Invoice = get(res.data, 'GetInvoiceDetailsForCompany');

        if (Invoice) {
            const responsePayload = {
                record: Invoice,
            };

            yield put(getInvoiceDataSuccessAction(responsePayload));
        } else {
            yield put(getInvoiceDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceDataErrorAction([errorMessage]));
    }
}

/**
 * Function calling the API for fetching the invoice data based on the given id.
 * @param param0
 */
function* handleGetOrganisationInvoiceDataRequest({
    payload: {
        invoiceId,
        region,
        isUsingCloudImportType,
        companyId,
        conversionCurrency,
        currencies
    },
}: any) {
    const errorMessage =
        'Error fetching invoice details. Please try again later.';

    try {

        const isCalendarView: boolean = yield select(
            (state: RootState) => getCompanyFlagValue(state, AtbViewType.CalendarView)
        );
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );
        const cloudImportType: string = yield select(getCloudImportType);

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_INVOICE_DETAILS_FOR_ORGANISATION,
            {
                CompanyId: companyId,
                InvoiceId: invoiceId,
                IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                IsCalendarView: isCalendarView,
                ConversionCurrency: conversionCurrency,
                CompanyIds: [companyId],
                OrganisationCurrencies: currencies
            }
        );

        const Invoice = get(res.data, 'GetInvoiceDetailsForOrganisation');

        if (Invoice) {
            const responsePayload = {
                record: Invoice,
            };

            yield put(getInvoiceDataSuccessAction(responsePayload));
        } else {
            yield put(getInvoiceDataErrorAction([errorMessage]));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceDataErrorAction([errorMessage]));
    }
}

/**
 * Function that checks if the invoices selected belong to a single customer or more.
 * @param param0
 */
function* handleCheckInvoicesIfSingleCustomer({ payload }: any) {
    const {
        filters,
        invoiceState,
        sortBy,
        sortAscending,
        pageSize,
        currentPage,
        callback,
    } = payload;

    try {
        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice'
        );

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.CHECK_INVOICES_IF_SINGLE_CUSTOMER,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * INVOICES_PAGE.pageSize,
                }
            }
        );

        const responseData = get(res.data, 'CheckInvoicesIfSingleCustomer');

        if (callback) {
            callback(responseData);
        }
    } catch (err) {
        console.log('err: ', err);

        if (callback) {
            callback(false);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for raising a ticket for single or multiple invoices.
 * @param param0
 */
function* handleRaiseQueryInvoiceBulkRequest({ payload: sagaPayload }: any) {
    const {
        InvoiceIds,
        CustomerId,
        CompanyId,
        TicketOptionId,
        WorkflowId,
        Details,
        InvoiceManagementFilter,
        callback,
    } = sagaPayload;
    
    if (InvoiceManagementFilter.CustomFieldFilters) {
        InvoiceManagementFilter.CustomFieldFilters = JSON.parse(
            InvoiceManagementFilter.CustomFieldFilters
        );
    } else if (InvoiceManagementFilter.CustomFieldMultipleValuesFilters) {
        InvoiceManagementFilter.CustomFieldMultipleValuesFilters = JSON.parse(
            InvoiceManagementFilter.CustomFieldMultipleValuesFilters
        );
    } 

    const payload = {
        Ticket: {
            CompanyId,
            WorkflowId,
            TicketOption: {
                Id: TicketOptionId,
            },
            Invoices: map(InvoiceIds, (Id) => ({
                Id,
            })),
            Customer: {
                Id: CustomerId,
            },
            Details,
        },
        InvoiceManagementFilter,
    };

    try {
        const { body } = yield call(post, {
            apiName: API_NAME,
            path: '/ticket/bulkinvoice',
            options: {
                body: payload
            }
        });

        const res: DynamicObject = yield body.json();
        const { TaskEditAction } = res;
        const successMessages: any = [];

        if (TaskEditAction) {
            successMessages.push(`Ticket has been sent successfully!`);
        }

        if (callback) {
            const response = {
                IsSuccess: true,
                Messages: successMessages,
            };
            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }


        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
}

/**
 * Function that sets the selected invoice id in the redux state for reference.
 * @param param0
 */
function* handleSetSelectedInvoiceIdRequest({ payload }: any) {
    const { invoiceId, callback } = payload;
    yield put(setSelectedInvoiceIdSuccessAction(invoiceId));

    callback();
}

/**
 * Function that fetches the conversation list of an invoice.
 * @param param0
 */
function* handleGetInvoiceConversationRequest({ payload }: any) {
    const errorMessage = `Error fetching invoice's conversation list. Please try again later.`;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice',
            true
        );

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CONVERSATION_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                }
            }
        );

        const { ConversationLines } = get(
            res.data,
            'GetConversationLinesForCompany'
        );
        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getInvoiceConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceConversationErrorAction([errorMessage]));
    }
}

/**
 * Function for fetching the conversation list of a given customer.
 * @param param0
 */
function* handleGetOrganisationInvoiceConversationRequest({
    payload: sagaPayload
}: any) {
    const errorMessage = `Error fetching invoice's conversation list. Please try again later.`;
    const { payload, region, companyIds } = sagaPayload;

    try {
        const { filters, sortBy, sortAscending, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'invoice',
            true
        );
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyGraphqlResponse,
            apiUrl,
            queries.GET_CONVERSATION_LINES_FOR_ORGANISATION,
            {
                ...cleanFilters,
                CompanyIds: companyIds,
                SortField: sortBy,
                Ascending: sortAscending,
                PageSize: pageSize,
                Skip: currentPage * DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
            }
        );
        
        const { ConversationLines } = get(res.data, 'GetConversationLinesForOrganisation');
        const Conversation = ConversationLines;

        if (Conversation) {
            const responsePayload = {
                data: Conversation,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(Conversation.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CONVERSATION_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getInvoiceConversationSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceConversationErrorAction([errorMessage]));
    }
}

/**
 * Function called for adding a comment in the invoice conversation list.
 * @param param0
 */
function* handleInvoiceAddCommentRequest({ payload: sagaPayload }: any) {
    const { filter, invoiceIds, excludeInvoices, comment, callback } =
        sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'invoice'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const payload = {
        InvoiceManagementFilter: {
            ...cleanFilters,
            InvoiceIds: invoiceIds,
            ExcludeInvoices: excludeInvoices,
        },
        Comment: comment,
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/conversation/save/invoicecomment',
            options: {
                body: payload
            }
        });

        if (callback) {
            let RefetchList = true;

            if (invoiceIds.length === 1 && excludeInvoices === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const invoicesUpdated: DynamicObject[] = map(
                    invoiceIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getInvoicesPageData);

                yield put(
                    getInvoicesSuccessAction({
                        data: invoicesUpdated as Invoice[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                // Use the error message if it's a standard Error object
                returnData = { Messages: [err.message] };
            } else {
                // Fallback if it's not an Error object
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        // Log the error in the console
        if (err instanceof Error) {
            console.log('Error:', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }

    //catch (err) {
    //    if (callback) {
    //        const returnData = get(err.response, 'data')
    //            ? err.response.data
    //            : { Messages: [err.message] };
    //        returnData.IsSuccess = false;
    //        callback(returnData);
    //    }
    //    if (err instanceof Error) {
    //        console.log('Error', err);
    //    } else {
    //        console.error('An unknown error occured.');
    //    }
    //}
}

/**
 * Function for adding comment to the customer's conversation list.
 * @param param0
 */
function* handleOrganisationInvoiceAddCommentRequest({ payload: sagaPayload }: any) {
    const { payload, region, companyIds } = sagaPayload;
    const { filter, invoiceIds, excludeInvoices, comment, callback } = payload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'invoice'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }

    const postPayload = {
        InvoiceManagementFilter: {
            ...cleanFilters,
            InvoiceIds: invoiceIds,
            CompanyIds: companyIds,
            ExcludeInvoices: excludeInvoices,
        },
        Comment: comment,
    };

    try {
        let apiUrl: string | undefined = undefined;

        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );

            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }

        if (apiUrl) {
            const currentSession: DynamicObject = yield call(fetchAuthSession);
            const accessToken = currentSession.tokens.accessToken;

            yield fetch(
                `${apiUrl}/conversation/save/invoicecomment`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify(postPayload),
                }
            );
        } else {
            yield call(post, {
                apiName: API_NAME,
                path: '/conversation/save/invoicecomment',
                options: {
                    body: postPayload
                }
            });
        }
        
        if (callback) {
            let RefetchList = true;

            if (invoiceIds.length === 1 && excludeInvoices === false) {
                RefetchList = false;
                const currentUser: DynamicObject = yield select(getCurrentUser);

                const invoicesUpdated: DynamicObject[] = map(
                    invoiceIds,
                    (uId: string) => {
                        return {
                            Id: uId,
                            ConversationLine: {
                                Content: comment,
                                CreatedDateTime: formatDateToDateObjectUTC(
                                    dayjs(),
                                    undefined,
                                    true
                                ),
                                User: {
                                    GivenName: get(
                                        currentUser,
                                        GivenNameAttribute
                                    ),
                                    FamilyName: get(
                                        currentUser,
                                        FamilyNameAttribute
                                    ),
                                },
                            },
                        };
                    }
                );

                const pageData: PageData = yield select(getInvoicesPageData);

                yield put(
                    getInvoicesSuccessAction({
                        data: invoicesUpdated as Invoice[],
                        pageData,
                        mergeData: true,
                    })
                );
            }

            const response = {
                IsSuccess: true,
                RefetchList,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    }
    //catch (err) {
    //    if (callback) {
    //        const returnData = get(err.response, 'data')
    //            ? err.response.data
    //            : { Messages: [err.message] };
    //        returnData.IsSuccess = false;
    //        callback(returnData);
    //    }
    //    if (err instanceof Error) {
    //        console.log('Error', err);
    //    } else {
    //        console.error('An unknown error occured.');
    //    }
    //}
}
/**
 * Function for checking the pause/resume state of invoices selected to enable/disable the `Pause/Resume` button in
 * Invoices page.
 * @param param0
 */
function* handleCheckInvoicesPauseResumeState({ payload }: any) {
    const {
        filters,
        sortBy,
        sortAscending,
        pageSize,
        currentPage,
        excludeInvoices,
        callback,
    } = payload;
    // To call async functions, use redux-saga's `call()`.
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filters,
        true,
        'invoice'
    );

    const sortObject = getSortFieldsWithCustomFields(sortBy);

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICE_SET_BLOCKED_STATE,
                variables: {
                    ...cleanFilters,
                    ...sortObject,
                    ExcludeInvoices: excludeInvoices,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * INVOICES_PAGE.pageSize,
                }
            }
        );

        const blockedStateResponse = get(res.data, 'GetInvoiceSetBlockedState');

        if (callback) {
            callback(blockedStateResponse);
        }
    } catch (err) {
        if (callback) {
            callback(false);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function called for handling invoices pause/resume action.
 * @param param0
 */
function* handleInvoicePauseResumeRequest({ payload: sagaPayload }: any) {
    const { filter, invoiceIds, excludeInvoices, isBlocked, isBlockedExpiryDate, ClearIsBlockedExpiryDate, callback } = sagaPayload;
    const cleanFilters = removeAppliedFiltersForApiRequest(
        filter,
        true,
        'invoice'
    );

    if (cleanFilters.CustomFieldFilters) {
        cleanFilters.CustomFieldFilters = JSON.parse(
            cleanFilters.CustomFieldFilters
        );
    } else if (cleanFilters.CustomFieldMultipleValuesFilters) {
        cleanFilters.CustomFieldMultipleValuesFilters = JSON.parse(
            cleanFilters.CustomFieldMultipleValuesFilters
        );
    }
    
    const payload = {
        InvoiceManagementFilter: {
            ...cleanFilters,
            InvoiceIds: invoiceIds,
            ExcludeInvoices: excludeInvoices,
        },
        IsBlocked: isBlocked,
        IsBlockedExpiryDate: isBlockedExpiryDate,
        ClearIsBlockedExpiryDate
    };

    try {
        yield call(post, {
            apiName: API_NAME,
            path: '/invoice/batch/save',
            options: {
                body: payload
            }
        });

        if (callback) {
            const response = {
                IsSuccess: true,
            };

            callback(response);
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    } 
    //catch (err) {
    //    if (callback) {
    //        const returnData = get(err.response, 'data')
    //            ? err.response.data
    //            : { Messages: [err.message] };
    //        returnData.IsSuccess = false;
    //        callback(returnData);
    //    }
    //    if (err instanceof Error) {
    //        console.log('Error', err);
    //    } else {
    //        console.error('An unknown error occured.');
    //    }
    //}
}

/**
 * Function for fetching the invoiceChanges list of a given invoice.
 * @param param0
 */
function* handleGetInvoiceChangesRequest({ payload }: any) {
    const errorMessage = "Error fetching invoice changes list. Please try again later.";

    try {
        const { filters, pageSize, currentPage } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CHANGE_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    PageSize: pageSize,
                    Skip:
                        currentPage * DETAILS_TAB.INVOICE_CHANGES_TIMELINE.pageSize,
                }
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.INVOICE_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getInvoiceChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceChangesErrorAction([errorMessage]));
    }
}


/**
 * Function for fetching the creditChanges list of a given credit.
 * @param param0
 */
function* handleGetOrganisationInvoiceChangesRequest({
    payload: sagaPayload
}: any) {
    const errorMessage = `Error fetching invoice changes list. Please try again later.`;
    const { payload, region, companyIds } = sagaPayload;
    
    try {
        const { filters, pageSize, currentPage } = payload;

        const currentSession: DynamicObject = yield call(fetchAuthSession);
        const accessToken = get(currentSession, 'tokens.accessToken');

        //yield fork(
        //    (subscriptionQuery, onNext, onError) =>
        //        SubscribeToUpdates<ChangeLinesFetchedData>(
        //            subscriptionQuery,
        //            null,
        //            onNext,
        //            onError,
        //            accessToken
        //        ),
        //    subscriptions.ON_CHANGE_LINES_FETCHED_FOR_ORGANISATION,
        //    InvoiceChangeLinesFetchedForOrganisationReceived({ pageSize, currentPage }),
        //    function handleError(error: any) {
        //        console.error('Subscription error:', JSON.stringify(error, null, 2));
        //    }
        //);
        
        const cleanFilters = removeAppliedFiltersForApiRequest(filters, true);

        let apiUrl: string | undefined = undefined;
        
        if (region) {
            const regionKeyConfig: DynamicObject[] = yield select(
                getRegionKeyConfig
            );
            const regionSettingsConfig: DynamicObject[] = yield select(
                getRegionSettingsConfig
            );

            const configUsed = getRegionConfigFromList(
                region,
                regionKeyConfig,
                regionSettingsConfig
            );
            apiUrl = get(configUsed, 'Url');
        }
        
        let res: DynamicObject;
        
        const variables = {
            ...cleanFilters,
            CompanyIds: companyIds,
            PageSize: pageSize,
            Skip:
                currentPage * DETAILS_TAB.CREDIT_CHANGES_TIMELINE.pageSize,
        };

        if (apiUrl) {
            const response: Response = yield fetch(
                `${apiUrl}/graphql`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: accessToken,
                    },
                    body: JSON.stringify({
                        query: getGraphqlQueryString(queries.GET_CHANGE_LINES_FOR_ORGANISATION),
                        variables,
                    }),
                }
            );

            res = yield response.json();
        } else {
            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_CHANGE_LINES_FOR_ORGANISATION,
                    variables: variables
                }
            );
        }

        const { ChangeLines } = get(res.data, 'GetChangeLinesForOrganisation');

        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(
                            pageSize <
                            DETAILS_TAB.CREDIT_CHANGES_TIMELINE.pageSize
                        ),
                },
            };

            yield put(getInvoiceChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getInvoiceChangesErrorAction([errorMessage]));
    }
}

/**
 * Function for getting the invoice custom fields for a company.
 * @param param0
 */
function* handleGetInvoiceCustomFieldsForCompanyRequest({ payload }: any) {
    const { 
            CompanyId, 
        callback,
        FieldName
        } = payload;

    try {
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICE_CUSTOM_FIELDS_FOR_COMPANY,
                variables: {
                    CompanyId: CompanyId,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues = get(res.data, 'GetInvoiceCustomFieldsForCompany');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) {
            callback([]);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/**
 * Function for getting the invoice custom fields for a company.
 * @param param0
 */
function* handleGetInvoiceCustomFieldsForOrganisationRequest({ payload }: any) {
    const errorMessage = '';
    const { 
            CompanyIds, 
            FieldName,
            callback
        } = payload;
    try {
        const errorMessage =
        'Error fetching custom field values. Please try again later.';
        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICE_CUSTOM_FIELDS_FOR_ORGANISATION,
                variables: {
                    CompanyIds: CompanyIds,
                    FieldNames: FieldName ? FieldName : []
                }
            }
        );

        const CustomFieldValues  = get(res.data, 'GetInvoiceCustomFieldsForOrganisation');
        
        if (callback) {
            CustomFieldValues.IsSuccess = true;
            callback(CustomFieldValues);
        }
    } catch (err) {
        if (callback) callback([]);
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.');
        }
    }
}

/*
 * Function that fetches the invoice list - api connection.
 * @param param0
 */
function* handleGetInvoicesForPaymentPlanRequestAction({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;

    try {
        const {
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            isUsingCloudImportType,
            usingMultipleWorkflow,
            usingCustomerWorkflow,
            isPaymentPlanEnabled,
            excludeInvoices,
            ...filters
        } = payload;
    
        const sortObject = getSortFieldsWithCustomFields(sortBy);
        let finalInvoices: DynamicObject[] = [];
        let fetching = true;
        let skip = 0;
        const cloudImportType: string = yield select(getCloudImportType);

        while (fetching) {
            // To call async functions, use redux-saga's `call()`.
            const res: DynamicObject = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_INVOICES_FOR_COMPANY,
                    variables: {
                        ...filters,
                        InvoiceState: invoiceState,
                        ...sortObject,
                        Ascending: sortAscending,
                        PageSize: pageSize,
                        Skip: skip * pageSize,
                        IsCloudImportType: cloudImportType != "MYOB" ? isUsingCloudImportType : false,
                        IsMultipleWorkflow: usingMultipleWorkflow,
                        UsingCustomerWorkflow: usingCustomerWorkflow,
                        PaymentPlanEnabled: isPaymentPlanEnabled,
                        ExcludeInvoices: excludeInvoices
                    }
                }
            );
            
            const { Invoices } = get(res.data, 'GetInvoicesForCompany');
            
            if (Invoices && Invoices.length > 0) {
                finalInvoices = [...finalInvoices, ...Invoices]
            } else {
                fetching = false;
            }

            skip = skip + 1;
        }
        
        if (finalInvoices && callback) {
            const response = {
                IsSuccess: true,
                Invoices: finalInvoices,
            };

            callback(response);            
        }
    } catch (err) {
        if (callback) {
            let returnData;

            if (err instanceof Error && 'response' in err && (err as any).response.body) {
                returnData = JSON.parse((err as any).response.body);
            } else if (err instanceof Error) {
                returnData = { Messages: [err.message] };
            } else {
                returnData = { Messages: ['An unknown error occurred.'] };
            }

            returnData.IsSuccess = false;
            callback(returnData);
        }

        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occurred.');
        }
    } 
    //catch (err: any) {
    //    if (callback) {
    //        const returnData = get(err.response, 'data')
    //            ? err.response.data
    //            : { Messages: [err.message] };
    //        returnData.IsSuccess = false;
    //        callback(returnData);
    //    }
    //    if (err instanceof Error) {
    //        console.log('Error', err);
    //    } else {
    //        console.error('An unknown error occured.');
    //    }
    //}
}

/**
 * Function that gets the invoices list for a certain customer.
 * @param param0
 */
function* handleGetCustomerInvoicesRequest({ payload }: any) {
    try {
        const {
            allCustomerIds,
            ...rest
        } = payload;

        const {
            filters,
            invoiceState,
            sortBy,
            sortAscending,
            pageSize,
            currentPage,
            hasPaymentPlan, 
            amountOperator,
            amountType,
            amountValue,
        } = rest;

        const MAX_EMPTY_INVOICE_RETRIES = 5;
        const triedCustomerIdsForEmptyInvoice: string[] = [];
        let emptyInvoiceRetryCount = 0;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'customer'
        );

        let res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_INVOICES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    InvoiceState: invoiceState,
                    SortField: sortBy,
                    Ascending: sortAscending,
                    PageSize: pageSize,
                    Skip: currentPage * INVOICES_PAGE.pageSize,
                    HasPaymentPlan: hasPaymentPlan,
                    AmountOperator: amountOperator,
                    AmountType: amountType,
                    AmountValue: amountValue
                }
            }
        );

        let { Invoices } = get(res.data, 'GetInvoicesForCompany', []);
        triedCustomerIdsForEmptyInvoice.push(filters.CustomerId);

        while (isEmpty(Invoices) && emptyInvoiceRetryCount < MAX_EMPTY_INVOICE_RETRIES) {
            const nextCustomerId = allCustomerIds?.find(
                (id: string) => !triedCustomerIdsForEmptyInvoice.includes(id)
            );

            if (!nextCustomerId) {
                console.log('No more customer IDs to retry for empty invoice');
                break;
            }

            console.log(
                `Empty invoice retry ${emptyInvoiceRetryCount + 1}/${MAX_EMPTY_INVOICE_RETRIES}: ` +
                `Trying customer ${nextCustomerId}`
            );

            // Update cleanFilters customerId for the next retry attempt
            cleanFilters.CustomerId = nextCustomerId;

            res = yield call(
                [client, client.graphql],
                {
                    query: queries.GET_INVOICES_FOR_COMPANY,
                    variables: {
                        ...cleanFilters,
                        InvoiceState: invoiceState,
                        SortField: sortBy,
                        Ascending: sortAscending,
                        PageSize: pageSize,
                        Skip: currentPage * INVOICES_PAGE.pageSize,
                        HasPaymentPlan: hasPaymentPlan,
                        AmountOperator: amountOperator,
                        AmountType: amountType,
                        AmountValue: amountValue
                    }
                }
            );

            const result = get(res.data, 'GetInvoicesForCompany', []);
            Invoices = result.Invoices;

            triedCustomerIdsForEmptyInvoice.push(nextCustomerId);
            emptyInvoiceRetryCount++;

            if (!isEmpty(Invoices)) {
                break;
            }
        }

        refetchCount = 0;

        if (!isEmpty(Invoices)) {
            yield put(updateInvoicesHaveNoPaymentPlanDataAction(Invoices));
        }

    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getCustomerInvoicesForCompanyRequestAction(payload));
        } else {
            yield put(getCustomerInvoicesForCompanyFailedAction());
        }
    }
}

function InvoicesFetchedForOrganisationReceived(params: { pageSize: number, currentPage: number }) {
    return function (data: InvoicesFetchedData) {
        dispatchAction(invoicesFetchedForOrganisationAction({ data, ...params }));
    };
}

function InvoiceConversationLinesFetchedForOrganisationReceived(params: { pageSize: number, currentPage: number }) {
    return function (data: ConversationLinesFetchedData) {
        dispatchAction(invoiceConversationLinesFetchedForOrganisationAction({ data, ...params }));
    };
}

function InvoiceChangeLinesFetchedForOrganisationReceived(params: { pageSize: number, currentPage: number }) {
    return function (data: ChangeLinesFetchedData) {
        dispatchAction(invoiceChangeLinesFetchedForOrganisationAction({ data, ...params }));
    };
}

function* watchInvoicesFetchedForOrganisationReceived() {
    yield takeLatest(invoicesFetchedForOrganisationAction.type, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const invoices: Invoice[] = data.OnInvoicesFetchedForOrganisation.Invoices;
        
        const responsePayload = {
            data: invoices,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage:
                    !(invoices.length < pageSize) &&
                    !(pageSize < INVOICES_PAGE.pageSize),
            },
        };

        refetchCount = 0;
        yield put(invoicesFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getInvoicesSuccessAction(responsePayload));
    });
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetInvoicesRequest() {
    yield takeLatest(
        getInvoicesRequestAction.type,
        handleGetInvoicesRequest
    );
}

function* watchGetInvoicesForOrganisationRequest() {
    yield takeLatest(
        getInvoicesForOrganisationRequestAction.type,
        handleGetInvoicesForOrganisationRequest
    );
}

function* watchGetInvoiceDataRequest() {
    yield takeLatest(
        getInvoiceDataRequestAction.type,
        handleGetInvoiceDataRequest
    );
}

function* watchGetOrganisationInvoiceDataRequest() {
    yield takeLatest(
        getOrganisationInvoiceDataRequestAction.type,
        handleGetOrganisationInvoiceDataRequest
    );
}

function* watchSetSelectedInvoiceIdRequest() {
    yield takeLatest(
        setSelectedInvoiceIdRequestAction.type,
        handleSetSelectedInvoiceIdRequest
    );
}

function* watchCheckInvoicesIfSingleCustomer() {
    yield takeLatest(
        checkInvoicesIfSingleCustomerAction.type,
        handleCheckInvoicesIfSingleCustomer
    );
}

function* watchRaiseQueryInvoiceBulkRequest() {
    yield takeLatest(
        raiseQueryInvoiceBulkRequestAction.type,
        handleRaiseQueryInvoiceBulkRequest
    );
}

function* watchGetInvoiceConversationRequest() {
    yield takeLatest(
        getInvoiceConversationRequestAction.type,
        handleGetInvoiceConversationRequest
    );
}

function* watchGetOrganisationInvoiceConversationRequest() {
    yield takeLatest(
        getOrganisationInvoiceConversationRequestAction.type,
        handleGetOrganisationInvoiceConversationRequest
    );
}

function* watchInvoiceAddCommentRequest() {
    yield takeLatest(
        invoiceAddCommentRequestAction.type,
        handleInvoiceAddCommentRequest
    );
}

function* watchOrganisationInvoiceAddCommentRequest() {
    yield takeLatest(
        organisationInvoiceAddCommentRequestAction.type,
        handleOrganisationInvoiceAddCommentRequest
    );
}

function* watchCheckInvoicesPauseResumeState() {
    yield takeLatest(
        checkInvoicesPauseResumeStateAction.type,
        handleCheckInvoicesPauseResumeState
    );
}

function* watchInvoicePauseResumeRequest() {
    yield takeLatest(
        invoicePauseResumeRequestAction.type,
        handleInvoicePauseResumeRequest
    );
}

function* watchGetInvoiceChangesRequest() {
    yield takeLatest(
        getInvoiceChangesRequestAction.type,
        handleGetInvoiceChangesRequest
    );
}

function* watchGetInvoiceCustomFieldsForCompanyRequest() {
    yield takeLatest(
        getInvoiceCustomFieldsForCompanyRequestAction.type,
        handleGetInvoiceCustomFieldsForCompanyRequest
    );
}

function* watchGetInvoiceCustomFieldsForOrganisationRequest() {
    yield takeLatest(
        getInvoiceCustomFieldsForOrganisationRequestAction.type,
        handleGetInvoiceCustomFieldsForOrganisationRequest
    );
}

function* watchGetOrganisationInvoiceChangesRequest() {
    yield takeLatest(
        getOrganisationInvoiceChangesRequestAction.type,
        handleGetOrganisationInvoiceChangesRequest
    );
}

// This is our watcher function. We use `take*()` functions to watch Redux for a specific action
// type, and run our saga, for example the `handleFetch()` saga above.
function* watchGetInvoicesForPaymentPlanRequestAction() {
    yield takeLatest(
        getInvoicesForPaymentPlanRequestAction.type,
        handleGetInvoicesForPaymentPlanRequestAction
    );
}

function* watchInvoiceConversationLinesFetchedForOrganisationReceived() {
    yield takeLatest(invoiceConversationLinesFetchedForOrganisationAction.type, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const conversationLines: Conversation[] = data.OnConversationLinesFetchedForOrganisation.ConversationLines;

        const responsePayload = {
            data: conversationLines,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(conversationLines.length < pageSize)
            },
        };

        yield put(invoiceConversationLinesFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getInvoiceConversationSuccessAction(responsePayload));
    });
}

function* watchInvoiceChangeLinesFetchedForOrganisationReceived() {
    yield takeLatest(invoiceChangeLinesFetchedForOrganisationAction.type, function* (action: any) {
        const { data, pageSize, currentPage } = action.payload;
        const changeLines: Invoice[] = data.OnChangeLinesFetchedForOrganisation.ChangeLines;

        const responsePayload = {
            data: changeLines,
            pageData: {
                pageSize: pageSize,
                currentPage: currentPage,
                hasNextPage: !(changeLines.length < pageSize)
            },
        };

        yield put(invoiceChangeLinesFetchedForOrganisationAction({ data, pageSize, currentPage }));
        yield put(getInvoiceChangeLinesSuccessAction(responsePayload));
    });
}

function* watchGetPaymentPlanConfigTemplatesRequest() {
    yield takeLatest(
        getPaymentPlanConfigTemplateRequestAction.type,
        handleGetPaymentPlanConfigTemplatesRequest
    );
}

function* watchGetInvoicesHaveNoPaymentPlanRequest() {
    yield takeLatest(
        getInvoicesHaveNoPaymentPlanRequestAction.type,
        handleGetInvoicesHaveNoPaymentPlanRequest
    );
}

function * watchSubmitBatchPaymentPlanRequest() {
    yield takeLatest(
        submitBatchPaymentPlanAction.type,
        handleSubmitBatchPaymentPlanRequest
    );
}

function* watchGetCustomersHaveNoPaymentPlanRequest() {
    yield takeLatest(
        getCustomersHaveNoPaymentPlanRequestAction.type,
        handleGetCustomersHaveNoPaymentPlanRequest
    );
}

function* watchGetCustomerInvoicesRequest() {
    yield takeLatest(
        getCustomerInvoicesForCompanyRequestAction.type,
        handleGetCustomerInvoicesRequest
    );
}

function* watchUpdateBatchPaymentPlanFormDataRequest() {
    yield takeLatest(
        updateBatchPaymentPlanFormDataRequestAction.type,
        handleUpdateBatchPaymentPlanFormData
    )
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* invoicesSaga() {
    yield all([
        fork(watchGetInvoicesRequest),
        fork(watchGetInvoicesForOrganisationRequest),
        fork(watchGetInvoiceDataRequest),
        fork(watchSetSelectedInvoiceIdRequest),
        fork(watchCheckInvoicesIfSingleCustomer),
        fork(watchRaiseQueryInvoiceBulkRequest),
        fork(watchGetInvoiceConversationRequest),
        fork(watchGetOrganisationInvoiceConversationRequest),
        fork(watchInvoiceAddCommentRequest),
        fork(watchCheckInvoicesPauseResumeState),
        fork(watchInvoicePauseResumeRequest),
        fork(watchGetInvoiceChangesRequest),
        fork(watchGetInvoiceCustomFieldsForCompanyRequest),
        fork(watchGetInvoiceCustomFieldsForOrganisationRequest),
        fork(watchGetInvoicesForPaymentPlanRequestAction),
        fork(watchGetOrganisationInvoiceChangesRequest),
        fork(watchGetOrganisationInvoiceDataRequest),
        fork(watchOrganisationInvoiceAddCommentRequest),
        fork(watchInvoicesFetchedForOrganisationReceived),
        fork(watchInvoiceConversationLinesFetchedForOrganisationReceived),
        fork(watchInvoiceChangeLinesFetchedForOrganisationReceived),
        fork(watchGetPaymentPlanConfigTemplatesRequest),
        fork(watchGetInvoicesHaveNoPaymentPlanRequest),
        fork(watchSubmitBatchPaymentPlanRequest),
        fork(watchGetCustomersHaveNoPaymentPlanRequest),
        fork(watchGetCustomerInvoicesRequest),
        fork(watchUpdateBatchPaymentPlanFormDataRequest)
    ]);
}

export default invoicesSaga;